import { Button, Drawer } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { GlobalOutlined, MenuOutlined } from '@ant-design/icons';
import { useLang } from '@app/lib/useLang';
import { BLOG_COPY, blogPath } from '@app/lib/blog';
import { COPY } from '@app/lib/content';
import { CONTACTS, LANGS, pagePath } from '@app/lib/site';
import style from './style.module.css';

/** Тот же путь на другом языке: /en/faro-quest/ → /pt/faro-quest/ */
function switchLangPath(pathname: string, lng: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && (LANGS as readonly string[]).includes(parts[0])) {
    parts[0] = lng;
    return `/${parts.join('/')}/`;
  }
  return `/${lng}/`;
}

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const { pathname } = useLocation();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 64);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Якоря главной. На других страницах ведут на главную нужного языка.
  const home = pagePath('home', lang);
  const isHome = pathname === home || pathname === home.slice(0, -1);
  const anchor = (hash: string) => (isHome ? hash : `${home}${hash}`);

  const navItems = [
    { key: 'quest', label: t('nav.quest'), href: anchor('#routes') },
    { key: 'how', label: t('nav.how'), href: anchor('#how') },
    { key: 'team', label: COPY[lang].ui.nav, href: pagePath('team', lang) },
    { key: 'blog', label: BLOG_COPY[lang].nav, href: blogPath(lang) },
    { key: 'faq', label: t('nav.faq'), href: anchor('#faq') },
  ];

  return (
    <header className={`${style.header} ${scrolled ? style.scrolled : ''}`}>
      <div className={style.bar}>
        <div className={style.left}>
          <nav className={style.langLinks} aria-label="Language">
            <GlobalOutlined className={style.logo} aria-hidden />
            {LANGS.map((lng) => (
              <a
                key={lng}
                href={switchLangPath(pathname, lng)}
                hrefLang={lng === 'pt' ? 'pt-PT' : lng}
                lang={lng === 'pt' ? 'pt-PT' : lng}
                className={`${style.langLink} ${lng === lang ? style.langActive : ''}`}
                aria-current={lng === lang ? 'true' : undefined}
              >
                {lng.toUpperCase()}
              </a>
            ))}
          </nav>
        </div>

        <nav className={style.center} aria-label="Primary">
          <ul className={style.navDesktop}>
            {navItems.map((item) => (
              <li key={item.key}>
                <a href={item.href} className={`${style.navLink} ${style.navText}`}>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={style.right}>
          <Button
            className={style.burger}
            type="text"
            aria-label={t('nav.openMenu', { defaultValue: 'Open menu' })}
            icon={<MenuOutlined />}
            onClick={() => setDrawerVisible(true)}
          />
        </div>
      </div>

      <Drawer
        placement="right"
        closable
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        styles={{ body: { paddingTop: 8, paddingBottom: 16 } }}
      >
        <nav className={style.navMobile} aria-label="Mobile">
          {navItems.map((item) => (
            <a key={item.key} href={item.href} className={style.navMobileItem} onClick={() => setDrawerVisible(false)}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className={style.contactsMobile}>
          <a href={CONTACTS.whatsappLink} target="_blank" rel="noopener noreferrer" onClick={() => setDrawerVisible(false)}>
            {CONTACTS.whatsappDisplay}
          </a>
          <a href={`mailto:${CONTACTS.email}`} onClick={() => setDrawerVisible(false)}>
            {CONTACTS.email}
          </a>
        </div>
      </Drawer>
    </header>
  );
};

export default Navbar;
